import {
  Anchor,
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  Modal,
  NumberInput,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import FlowStepIcon from './FlowStepIcon';

import { IconAlertTriangle, IconExternalLink, IconEye, IconMaximize, IconMoon, IconPencil } from '@tabler/icons-react';
import prettifiedLabel from '../../constants/labelPrettifier';
import behavioralRuleFilters from '../../constants/Rules/behaviorRuleFilters';
import behavioralRules from '../../constants/Rules/behaviorSegmentRules';
import useFetch from '../../helpers/useFetch';
import EmailsDetailsForm from '../Emails/EmailsDetailsForm';
import ErrorMessage from '../ErrorMessage';
import FacebookAdForm from '../Facebook/Ads/FacebookAdForm';
import GoogleAdForm from '../Google/Ads/GoogleAdForm';

import PushNotificationForm from '../PushNotifications/PushNotificationForm';
import Rules from '../Rules/Rules';
import SearchableCombobox from '../SearchableCombobox';
import TextMessageForm from '../TextMessages/TextMessageForm';
import ViberMessageForm from '../ViberMessages/ViberMessageForm';
import WebhookForm from '../Webhooks/WebhookForm';
import WhatsappMessageForm from '../Whatsapp/WhatsappMessageForm';

const FlowSidebarSelection = ({
  selectedNode,
  updateItem,
  campaigns,
  webhooks,
  pushNotifications,
  textMessages,
  viberMessages,
  emails,
  availableCustomFields,
  eventTags,
  eventActions,
  flowTypeOptions,
  readOnly,
  pages,
  subscriptions,
  segments,
  whatsappMessages,
  surveys,
  remainingDailyQuotaWhatsappMessages,
}) => {
  const { domain } = useSelector((state) => state.domain);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const [channelType, setChannelType] = useState();
  const [channelContent, setChannelContent] = useState(null);
  const [error, setError] = useState();
  const fetch = useFetch();
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  let selectionConfig;

  const updateNodeIdField = (node, updateItem) => {
    const newNode = JSON.parse(JSON.stringify(node));
    const originalIdValue = newNode.data.custom.values?.id;

    if (newNode.data.custom.values && newNode.data.custom.values.id) {
      newNode.data.custom.values.id = '';
    }

    updateItem(newNode);

    return { newNode, originalIdValue };
  };

  const restoreNodeIdField = (node, originalIdValue, updateItem) => {
    const newNode = JSON.parse(JSON.stringify(node));

    if (newNode.data.custom.values && originalIdValue !== undefined) {
      newNode.data.custom.values.id = originalIdValue;
    }

    updateItem(newNode);
  };

  const setChannel = async (id, channelType) => {
    setChannelType(channelType);
    if (id && channelType) {
      fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/${channelType}/${id}`)
        .then((data) => {
          setChannelContent(data);
          openModal();
        })
        .catch(setError);
    }
  };

  if (!selectedNode) {
    return '';
  }

  const handleChange = (e, options) => {
    const newNode = JSON.parse(JSON.stringify(selectedNode));
    newNode.data.custom.values[e.target.name] = e.target.value;
    if (options) {
      newNode.data.custom.labels = newNode.data.custom.labels || {};
      newNode.data.custom.labels[e.target.name] = (options.find((o) => o.value === e.target.value) || {}).label;
    }
    updateItem(newNode);
  };

  const updateContentType = (contentData) => {
    const { newNode, originalIdValue } = updateNodeIdField(selectedNode, updateItem);
    fetch(
      `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/${channelType}/${
        channelContent[channelType.slice(0, -1)].id
      }`,
      {
        method: 'PUT',
        body: JSON.stringify(contentData),
      }
    ).then(() => {
      restoreNodeIdField(newNode, originalIdValue, updateItem);
      closeModal();
    });
  };

  const getTypeFromKey = (key) => {
    const typeMap = {
      sendEmail: 'emails',
      sendTextMessage: 'textMessages',
      sendPushNotification: 'pushNotifications',

      sendFacebookAd: 'facebookAds',
      sendViberMessage: 'viberMessages',
      sendWhatsappMessage: 'whatsappMessages',
      sendGoogleAd: 'googleAds',
      sendWebhook: 'webhooks',
    };
    return typeMap[key] || '';
  };

  const DAY_LABELS = {
    Monday: 'M', Tuesday: 'T', Wednesday: 'W', Thursday: 'T', Friday: 'F', Saturday: 'S', Sunday: 'S',
  };

  const configFields = [];
  const dayConfigs = [];
  let hasObserveQuietHours = false;

  // Normalize legacy wait nodes that have no waitMode (derived, not mutated)
  const values = selectedNode.data.custom.key === 'wait'
    ? { waitMode: 'static', ...selectedNode.data.custom.values }
    : selectedNode.data.custom.values;

  for (const config of selectedNode.data.custom.config) {
    const value = values[config.field];

    // Collect day-of-week checkboxes separately
    if (config.field && config.field.startsWith('runOn')) {
      const dayName = config.field.replace('runOn', '');
      dayConfigs.push({ field: config.field, dayName, enabled: !!value });
      continue;
    }
    // Skip "Send on" section header
    if (config.type === 'sectionStart' && config.name === 'Send on') {
      continue;
    }
    // Skip empty separator before observeQuietHours
    if (config.type === 'sectionStart' && config.name === '') {
      continue;
    }
    // Collect observeQuietHours separately
    if (config.field === 'observeQuietHours') {
      hasObserveQuietHours = true;
      continue;
    }

    if (config.field === 'lookalikeRatio' && values.lookalikeType !== 'custom_ratio') {
      continue;
    }

    if (config.showWhen) {
      const currentValue = values[config.showWhen.field];
      if (currentValue !== config.showWhen.value) {
        continue;
      }
    }

    let configControl;
    if (config.type === 'number') {
      configControl = (
        <NumberInput
          disabled={readOnly}
          size="sm"
          min={config.min}
          max={config.max}
          step={config.step}
          name={config.field}
          value={value || ''}
          label={config.name}
          error={
            (config.min !== undefined && value !== undefined && value < config.min) ||
            (config.max !== undefined && value !== undefined && value > config.max)
          }
          onChange={(val) => handleChange({ target: { name: config.field, value: val } })}
          rightSection={
            config.detailsSuffix ? (
              <Text size="sm" c="dimmed">
                {config.detailsSuffix}
              </Text>
            ) : undefined
          }
          rightSectionWidth={config.detailsSuffix ? 50 : undefined}
        />
      );
    } else if (config.type === 'string') {
      const isValidMergeTag = config.field === 'mergeTag'
        ? /^\{\{[^}]+\}\}$/.test(value || '')
        : true;
      configControl = (
        <TextInput
          disabled={readOnly}
          size="sm"
          name={config.field}
          value={value || ''}
          error={value !== undefined && (!value || !isValidMergeTag)}
          onChange={(e) => handleChange(e)}
        />
      );
    } else if (config.type === 'sectionStart') {
      configControl = (
        <>
          <hr />
        </>
      );
    } else if (config.type === 'select') {
      const options = flowTypeOptions[selectedNode.data.custom.key][config.field];

      configControl = (
        <SearchableCombobox
          disabled={readOnly}
          value={value?.toString()}
          options={options}
          onChange={(value) => handleChange({ target: { name: config.field, value } }, options)}
        />
      );
    } else if (config.type === 'checkbox') {
      configControl = (
        <Checkbox
          disabled={readOnly}
          name={config.field}
          checked={value || false}
          onChange={() => handleChange({ target: { name: config.field, value: !value } })}
        />
      );
    } else if (config.type === 'segment') {
      const ruleCount = (value || []).length;
      // Stays clickable while the workflow is running - the modal opens read-only
      // so the configured rules can still be inspected.
      configControl = (
        <Button
          variant="light"
          fullWidth
          leftSection={readOnly ? <IconEye size={16} /> : <IconMaximize size={16} />}
          onClick={() => setIsRulesModalOpen(true)}
        >
          {readOnly ? 'View Rules' : 'Edit Rules'}
          {ruleCount > 0 ? ` (${ruleCount})` : ''}
        </Button>
      );
    }

    if (config.name && config.name !== 'Send on') {
      let labelColSize = 4;
      let valueColSize = 6;

      if (config.type === 'string') {
        labelColSize = 12;
        valueColSize = 12;
      }
      if (config.type === 'select') {
        configFields.push(
          <Grid key={selectedNode.data.custom.config.indexOf(config)} style={{ marginBottom: '10px' }}>
            <Grid.Col span={config.field === 'id' ? 9 : 12}>{configControl}</Grid.Col>
            {config.field === 'id' && (
              <Grid.Col span={3} style={{ textAlign: 'right' }}>
                <Button
                  onClick={() =>
                    setChannel(selectedNode.data.custom.values.id, getTypeFromKey(selectedNode.data.custom.key))
                  }
                  // The modal it opens is a fully editable channel form, so it stays locked while the
                  // workflow runs - same rule as the campaign form's Edit button.
                  disabled={!selectedNode.data.custom.values.id || readOnly}
                >
                  <IconPencil size={14} />
                </Button>
              </Grid.Col>
            )}
          </Grid>
        );
      } else if (config.type === 'number') {
        configFields.push(
          <Grid key={selectedNode.data.custom.config.indexOf(config)} mt="sm">
            <Grid.Col span={12}>{configControl}</Grid.Col>
          </Grid>
        );
      } else {
        configFields.push(
          <Grid key={selectedNode.data.custom.config.indexOf(config)} mt="sm">
            <Grid.Col span={labelColSize}>
              <Text size="sm">{config.name}</Text>
            </Grid.Col>
            <Grid.Col span={valueColSize}>{configControl}</Grid.Col>
          </Grid>
        );
      }
    } else {
      if (config.name !== 'Send on') {
        configFields.push(
          <Grid key={selectedNode.data.custom.config.indexOf(config)}>
            <Grid.Col span={12}>{configControl}</Grid.Col>
          </Grid>
        );
      }
    }
  }

  // Build compact day toggles + quiet hours row
  if (dayConfigs.length > 0 || hasObserveQuietHours) {
    configFields.push(
      <Box key="send-on-section" mt="sm">
        <Divider mb="sm" />
        {dayConfigs.length > 0 && <Text size="sm" fw={500} mb={6}>Send on</Text>}
        <Group gap={6} justify="center">
          {dayConfigs.map(({ field, dayName, enabled }) => (
            <Box
              key={field}
              onClick={() => !readOnly && handleChange({ target: { name: field, value: !enabled } })}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: readOnly ? 'default' : 'pointer',
                background: enabled ? 'var(--mantine-color-primary-filled)' : 'var(--mantine-color-gray-1)',
                color: enabled ? '#fff' : 'var(--mantine-color-gray-5)',
                fontWeight: enabled ? 700 : 400,
                fontSize: 13,
                transition: 'all 0.15s ease',
                userSelect: 'none',
              }}
            >
              {DAY_LABELS[dayName]}
            </Box>
          ))}
          {hasObserveQuietHours && (
            <Tooltip label="Wait until end of quiet hours" withArrow>
              <Box
                onClick={() =>
                  !readOnly &&
                  handleChange({
                    target: { name: 'observeQuietHours', value: !selectedNode.data.custom.values.observeQuietHours },
                  })
                }
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: readOnly ? 'default' : 'pointer',
                  background: selectedNode.data.custom.values.observeQuietHours
                    ? 'var(--mantine-color-primary-filled)'
                    : 'var(--mantine-color-gray-1)',
                  color: selectedNode.data.custom.values.observeQuietHours
                    ? '#fff'
                    : 'var(--mantine-color-gray-5)',
                  transition: 'all 0.15s ease',
                  userSelect: 'none',
                }}
              >
                <IconMoon size={14} />
              </Box>
            </Tooltip>
          )}
        </Group>
      </Box>
    );
  }

  selectionConfig = (
    <>
      <Group justify="center" gap="xs" mb="sm">
        <FlowStepIcon icon={selectedNode.data.custom.key} />
        <Title order={5}>{selectedNode.data.label}</Title>
      </Group>
      <Divider mb="sm" />
      {selectedNode.data.custom.disclaimer && (
        <Text size="xs" c="dimmed" mb="sm">
          <IconAlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {selectedNode.data.custom.disclaimer}
        </Text>
      )}
      {configFields}
      {selectedNode.data.custom.documentation && (
        <Box mb="sm">
          <Anchor href={selectedNode.data.custom.documentation.url} target="_blank" size="xs">
            {selectedNode.data.custom.documentation.label}{' '}
            <IconExternalLink size={14} style={{ verticalAlign: 'middle' }} />
          </Anchor>
        </Box>
      )}
      <Divider mb="sm" />
      {Number.isFinite(remainingDailyQuotaWhatsappMessages) && selectedNode.data.custom.disclaimerLimit && (
        <Text size="xs" c="yellow" mb="sm">
          <IconAlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {`If the selected segment for your workflow exceeds the remaining WhatsApp message quota of ${remainingDailyQuotaWhatsappMessages} messages, messages will either not be sent or will be rescheduled for the next day, depending on the "Send strategy" setting configured in your Releva Facebook&WhatsApp Settings.`}
        </Text>
      )}
    </>
  );

  return (
    <Box px="xs">
      {selectionConfig}
      <Modal
        opened={isModalOpen}
        onClose={closeModal}
        size="full"
        styles={{ content: { width: '100%', maxWidth: '100%' } }}
        title={`Edit ${prettifiedLabel(channelType)}`}
      >
        {channelContent ? (
          channelType === 'viberMessages' ? (
            <ViberMessageForm
              viberMessage={channelContent.viberMessage}
              saveViberMessage={updateContentType}
              onBack={closeModal}
            />
          ) : channelType === 'emails' ? (
            <EmailsDetailsForm
              email={channelContent.email}
              onUpdate={() => {
                const { newNode, originalIdValue } = updateNodeIdField(selectedNode, updateItem);
                //we need the timeout as we have to trigger state update to rerender the preview
                setTimeout(() => {
                  restoreNodeIdField(newNode, originalIdValue, updateItem);
                  closeModal();
                }, 50);
              }}
              onBack={closeModal}
            />
          ) : channelType === 'textMessages' ? (
            <TextMessageForm
              textMessage={channelContent.textMessage}
              saveTextMessage={updateContentType}
              onUpdate={updateContentType}
              onBack={closeModal}
            />
          ) : channelType === 'pushNotifications' ? (
            <PushNotificationForm
              pushNotification={channelContent.pushNotification}
              savePushNotification={updateContentType}
              onBack={closeModal}
            />
          ) : channelType === 'whatsappMessages' ? (
            <WhatsappMessageForm
              whatsappMessage={channelContent.whatsappMessage}
              saveWhatsappMessage={updateContentType}
              onBack={closeModal}
            />
          ) : channelType === 'facebookAds' ? (
            <FacebookAdForm
              facebookAd={channelContent.facebookAd}
              saveFacebookAd={updateContentType}
              onBack={closeModal}
            />
          ) : channelType === 'googleAds' ? (
            <GoogleAdForm googleAd={channelContent.googleAd} saveGoogleAd={updateContentType} onBack={closeModal} />
          ) : channelType === 'webhooks' ? (
            <WebhookForm webhook={channelContent.webhook} saveWebhook={updateContentType} onBack={closeModal} />
          ) : (
            <p>No valid content type available.</p>
          )
        ) : (
          <p>Loading content, please wait...</p>
        )}
        {error && <ErrorMessage message={error.message} />}
      </Modal>
      {selectedNode.data.custom.config.some((c) => c.type === 'segment') && (
        <Modal
          opened={isRulesModalOpen}
          onClose={() => setIsRulesModalOpen(false)}
          size="xl"
          title={readOnly ? 'Condition Rules (read-only)' : 'Edit Condition Rules'}
        >
          <Rules
            readOnly={readOnly}
            campaigns={campaigns}
            webhooks={webhooks}
            emails={emails}
            textMessages={textMessages}
            viberMessages={viberMessages}
            pushNotifications={pushNotifications}
            availableCustomFields={availableCustomFields}
            rules={
              selectedNode.data.custom.values[
                selectedNode.data.custom.config.find((c) => c.type === 'segment').field
              ] || []
            }
            updateRules={(rules) => {
              handleChange({
                target: {
                  name: selectedNode.data.custom.config.find((c) => c.type === 'segment').field,
                  value: rules,
                },
              });
            }}
            eventTags={eventTags}
            eventActions={eventActions}
            pages={pages}
            subscriptions={subscriptions}
            availableRules={behavioralRules}
            availableFilters={behavioralRuleFilters}
            segments={segments}
            whatsappMessages={whatsappMessages}
            surveys={surveys}
          />
        </Modal>
      )}
      <br />
      <br />
    </Box>
  );
};

export default FlowSidebarSelection;
